import { useState, useCallback, useRef, useEffect } from 'react';
import type { VisualField } from '../../../types/wizardTypes';
import { fieldsToSchema, schemaToFields } from '../converters/fieldSchemaConverter';
import { safeJsonParse, safeJsonStringify } from '../../../utils/security';
import { useConnectionsStore } from '../../../store/connectionsStore';

interface UseFieldBuilderOptions {
  watch: (field: string) => unknown;
  setValue: (field: string, value: unknown, options?: { shouldDirty?: boolean }) => void;
}

export function useFieldBuilder({ watch, setValue }: UseFieldBuilderOptions) {
  const [fields, setFieldsState] = useState<VisualField[]>([]);
  const hydrated = useRef(false);
  const suppressSync = useRef(false);
  const prevFieldNamesRef = useRef<Map<string, string>>(new Map());
  const connectors = useConnectionsStore(s => s.connectors);
  const isMssql = connectors.find(c => c.id === watch('mainConnectionId'))?.connectionTypeId === 7;

  // Hydrate from inputSchema on mount
  useEffect(() => {
    if (hydrated.current) return;
    const raw = watch('inputSchema') as string;
    if (raw && raw !== '{}' && raw !== '') {
      const parsed = safeJsonParse(raw, {});
      if (parsed.success && parsed.data) {
        const hydFields = schemaToFields(parsed.data, isMssql);
        if (hydFields.length > 0) {
          suppressSync.current = true;
          setFieldsState(hydFields);
          prevFieldNamesRef.current = new Map(hydFields.map(f => [f.id, f.name]));
        }
      } else {
        suppressSync.current = true;
      }
    }
    hydrated.current = true;
  }, []);

  // Sync fields → inputSchema (skip if suppressed from hydration)
  // Also keep prevFieldNamesRef in sync for rename detection
  useEffect(() => {
    if (suppressSync.current) {
      suppressSync.current = false;
      return;
    }
    const schema = fieldsToSchema(fields);
    setValue('inputSchema', safeJsonStringify(schema, '{}'), { shouldDirty: true });
    prevFieldNamesRef.current = new Map(fields.map(f => [f.id, f.name]));
  }, [fields]);

  const addField = useCallback(() => {
    const newField: VisualField = {
      id: crypto.randomUUID(),
      name: '',
      label: '',
      type: 'string',
      description: '',
      required: false,
      example: '',
    };
    setFieldsState(prev => [...prev, newField]);
  }, []);

  const updateField = useCallback((id: string, updates: Partial<VisualField>) => {
    // When a field name changes, update {{ oldName }} → {{ newName }} in bodyTemplate
    if (updates.name !== undefined) {
      const oldName = prevFieldNamesRef.current.get(id);
      if (oldName && oldName !== updates.name) {
        const bodyTemplate = (watch('bodyTemplate') as string) || '';
        if (bodyTemplate) {
          const escaped = oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const pattern = new RegExp(
            `(\\{\\{\\s*)${escaped}(\\s*(?:\\|[^}]*)?\\s*\\}\\})`,
            'g'
          );
          const patched = bodyTemplate.replace(pattern, `$1${updates.name}$2`);
          if (patched !== bodyTemplate) {
            setValue('bodyTemplate', patched, { shouldDirty: true });
          }
        }
        prevFieldNamesRef.current.set(id, updates.name);
      }
    }
    setFieldsState(prev =>
      prev.map(f => (f.id === id ? { ...f, ...updates } : f))
    );
  }, [watch, setValue]);

  const removeField = useCallback((id: string) => {
    setFieldsState(prev => prev.filter(f => f.id !== id));
  }, []);

  const moveField = useCallback((fromIndex: number, toIndex: number) => {
    setFieldsState(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  }, []);

  const setFields = useCallback((newFields: VisualField[]) => {
    setFieldsState(newFields);
  }, []);

  // Sync from advanced JSON editor back to visual fields
  const syncFromSchema = useCallback((schemaJson: string) => {
    if (!schemaJson.trim() || schemaJson.trim() === '{}') {
      suppressSync.current = true;
      setFieldsState([]);
    } else {
      const parsed = safeJsonParse(schemaJson, {});
      if (parsed.success && parsed.data) {
        suppressSync.current = true;
        setFieldsState(schemaToFields(parsed.data, isMssql));
      }
    }
    setValue('inputSchema', schemaJson, { shouldDirty: true });
  }, [setValue, isMssql]);

  return {
    fields,
    addField,
    updateField,
    removeField,
    moveField,
    setFields,
    syncFromSchema,
  };
}
